import { WORKFLOW_PATH_LABEL_MAX } from "@sagot/constants";
import { freshStepId, nextPathId, stepIds } from "./definition-model";
import { exitSlotsOf, type ExitRef } from "./exit-slots";
import { copyNotes } from "./notes-model";
import type { Definition, PathDef, Step, StepTranslation, Translations } from "./types";

/** What the author does to a whole path: name it, copy it, and see what leads to it before deleting it. */

/** The path's own name, trimmed and cut to `Path.label`'s limit; an empty name removes the label. */
export function renamePath(definition: Definition, pathId: string, label: string): Definition {
  const path = definition.paths[pathId];
  if (!path) return definition;
  const name = label.trim().slice(0, WORKFLOW_PATH_LABEL_MAX).trim();
  if ((path.label ?? "") === name) return definition;
  const renamed: PathDef = name ? { ...path, label: name } : { steps: path.steps };
  return { ...definition, paths: { ...definition.paths, [pathId]: renamed } };
}

/** Every exit of another path that leads to `pathId`: what deleting the path clears. */
export function incomingConnections(definition: Definition, pathId: string): ExitRef[] {
  const found: ExitRef[] = [];
  for (const [id, path] of Object.entries(definition.paths)) {
    if (id === pathId) continue;
    path.steps.forEach((step, index) => {
      for (const slot of exitSlotsOf(step)) if (slot.target === pathId) found.push({ path: id, index, key: slot.key });
    });
  }
  return found;
}

/** `send_message_3` → `send_message`: a copy numbers from the step's kind, not from the number it already carries. */
const baseOf = (id: string): string => id.replace(/_\d+$/, "") || id;

/** The step with new ids for itself and, in Apply rules, for the actions inside it. */
function cloneStep(step: Step, claim: (id: string) => string): Step {
  const copy = structuredClone(step);
  copy.id = claim(step.id);
  if (copy.type === "apply_rules") for (const rule of copy.rules) for (const action of rule.actions) action.id = claim(action.id);
  return copy;
}

/** The translations of the steps that were copied, repeated for their copies. */
function cloneTranslations(translations: Translations | undefined, renamed: ReadonlyMap<string, string>): Translations | undefined {
  if (!translations) return undefined;
  const cloned: Translations = {};
  for (const [language, byStep] of Object.entries(translations)) {
    const added: Record<string, StepTranslation> = {};
    for (const [stepId, texts] of Object.entries(byStep)) {
      const copyId = renamed.get(stepId);
      if (copyId) added[copyId] = structuredClone(texts);
    }
    cloned[language] = { ...byStep, ...added };
  }
  return cloned;
}

export interface DuplicatedPath {
  definition: Definition;
  /** The letter of the copy. */
  pathId: string;
}

/**
 * A copy of a path under the next free letter, named `copyLabel`. Its steps get fresh ids (also the actions inside Apply
 * rules), the translations and notes of the original are copied along, and its exits lead where the original's did.
 * `null` when the path does not exist or all 26 letters are used.
 */
export function duplicatePath(definition: Definition, pathId: string, copyLabel: string): DuplicatedPath | null {
  const source = definition.paths[pathId];
  const target = nextPathId(definition);
  if (!source || !target) return null;
  const taken = stepIds(definition);
  const renamed = new Map<string, string>();
  const claim = (id: string): string => {
    const fresh = freshStepId(baseOf(id), taken);
    taken.add(fresh);
    renamed.set(id, fresh);
    return fresh;
  };
  const steps = source.steps.map((step) => cloneStep(step, claim));
  const label = copyLabel.trim().slice(0, WORKFLOW_PATH_LABEL_MAX).trim();
  const copy: PathDef = label ? { label, steps } : { steps };
  const translations = cloneTranslations(definition.translations, renamed);
  const withPath: Definition = { ...definition, paths: { ...definition.paths, [target]: copy }, ...(translations ? { translations } : {}) };
  return { definition: copyNotes(withPath, pathId, target), pathId: target };
}
